import { ElMessage } from "element-plus";
import { shouldSuppressPowerInsufficientError } from "@/hooks/usePowerInsufficientDialog";

/** 全局拦截器已 toast 时打在 reject 对象上的标记 */
export const HTTP_ERROR_TOAST_SHOWN_FLAG = "__httpErrorToastShown__";

type ErrorLike = Record<string, unknown>;

/** 全局拦截器 toast 后打标，供业务层判断是否重复弹错 */
export function markHttpErrorToastShown<T extends object>(error: T): T & { [HTTP_ERROR_TOAST_SHOWN_FLAG]: true } {
  return {
    ...error,
    [HTTP_ERROR_TOAST_SHOWN_FLAG]: true,
  };
}

/** 全局拦截器是否已为该错误弹过 toast */
export function isHttpErrorToastShown(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  return (error as ErrorLike)[HTTP_ERROR_TOAST_SHOWN_FLAG] === true;
}

/** 从 reject 体 / Error / AxiosError 提取可读 msg，空则返回 fallback */
export function getHttpErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }
  if (error && typeof error === "object") {
    const payload = error as ErrorLike;
    if (typeof payload.msg === "string" && payload.msg.trim()) {
      return payload.msg.trim();
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
    const responseData = payload.response as { data?: { msg?: string; message?: string } } | undefined;
    const responseMsg = responseData?.data?.msg || responseData?.data?.message;
    if (typeof responseMsg === "string" && responseMsg.trim()) {
      return responseMsg.trim();
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

/** 从 reject 体 / AxiosError 提取业务错误码（如 SERVICE-SLICE-00122） */
export function getHttpErrorCode(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "";
  }
  const payload = error as ErrorLike;
  if (typeof payload.code === "string" && payload.code.trim()) {
    return payload.code.trim();
  }
  const responseData = payload.response as { data?: { code?: unknown } } | undefined;
  const nested = responseData?.data?.code;
  if (typeof nested === "string" && nested.trim()) {
    return nested.trim();
  }
  return "";
}

function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const payload = error as ErrorLike;
  if (payload.code === 401) {
    return true;
  }
  const response = payload.response as { status?: number } | undefined;
  return response?.status === 401;
}

function isUserCancelledError(error: unknown): boolean {
  return error === "cancel" || error === "close";
}

/** 是否应跳过业务 toast（已弹 / 影豆不足 / 401 会话失效 / 用户取消 confirm） */
export function shouldSuppressRequestErrorToast(error: unknown): boolean {
  if (isHttpErrorToastShown(error)) {
    return true;
  }
  if (shouldSuppressPowerInsufficientError(error)) {
    return true;
  }
  if (isUnauthorizedError(error)) {
    return true;
  }
  if (isUserCancelledError(error)) {
    return true;
  }
  return false;
}

/** 业务层入口：未 suppress 时才 ElMessage.error */
export function showRequestError(error: unknown, fallback: string): void {
  if (shouldSuppressRequestErrorToast(error)) {
    return;
  }
  ElMessage.error(getHttpErrorMessage(error, fallback));
}
